import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL, setCustomerToken, customerFetch } from '../utils/api';
import { useStore } from '../context/StoreContext';
import { IconStar, IconPlus, IconCheck } from '../components/icons';
import './AccountPage.css';

const formatPhone = (value) =>
  (value || '').replace(/\D/g, '').slice(0, 10).match(/.{1,2}/g)?.join(' ') || '';

// Carte « Mon compte » : le client édite ses infos et change son mot de passe
function MyAccountCard() {
  const { customer, setCustomer } = useStore();
  const [tab, setTab] = useState(null); // null | 'infos' | 'password'
  const [info, setInfo] = useState({
    name: customer.name,
    phone: formatPhone(customer.phone),
    email: customer.email || '',
    marketingOptin: customer.marketingOptin ?? true
  });
  const [pwd, setPwd] = useState({ currentPassword: '', newPassword: '' });
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  const saveInfo = async (e) => {
    e.preventDefault();
    setErr(''); setMsg(''); setSaving(true);
    try {
      const res = await customerFetch('/customers/me', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(info)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setCustomer(data.customer);
        setMsg('Vos informations ont été mises à jour.');
        setTab(null);
      } else {
        setErr(data.errors?.[0]?.msg || data.message || 'Erreur');
      }
    } catch { setErr('Erreur de connexion'); }
    finally { setSaving(false); }
  };

  const savePwd = async (e) => {
    e.preventDefault();
    setErr(''); setMsg(''); setSaving(true);
    try {
      const res = await customerFetch('/customers/me/password', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(pwd)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setMsg('Mot de passe modifié.');
        setPwd({ currentPassword: '', newPassword: '' });
        setTab(null);
      } else {
        setErr(data.errors?.[0]?.msg || data.message || 'Erreur');
      }
    } catch { setErr('Erreur de connexion'); }
    finally { setSaving(false); }
  };

  return (
    <div className="card card-pad compte-mon-compte">
      <h3>Mon compte</h3>
      {msg && <div className="compte-msg-ok">{msg}</div>}

      {tab === null && (
        <div className="compte-actions-btns">
          <button className="btn btn-ghost btn-sm" onClick={() => { setTab('infos'); setErr(''); }}>
            Modifier mes infos
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => { setTab('password'); setErr(''); }}>
            Changer mon mot de passe
          </button>
        </div>
      )}

      {tab === 'infos' && (
        <form onSubmit={saveInfo}>
          <div className="field"><label>Nom</label>
            <input type="text" value={info.name} onChange={(e) => setInfo({ ...info, name: e.target.value })} required />
          </div>
          <div className="field"><label>Téléphone</label>
            <input type="tel" value={info.phone}
              onChange={(e) => setInfo({ ...info, phone: formatPhone(e.target.value) })} required />
          </div>
          <div className="field"><label>Email</label>
            <input type="email" value={info.email} onChange={(e) => setInfo({ ...info, email: e.target.value })} />
          </div>
          <label className="auth-optin">
            <input type="checkbox" checked={info.marketingOptin}
              onChange={(e) => setInfo({ ...info, marketingOptin: e.target.checked })} />
            Recevoir les offres du Fief par email
          </label>
          {err && <div className="msg-erreur" style={{ marginBottom: 12 }}>{err}</div>}
          <div className="compte-actions-btns">
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setTab(null)}>Annuler</button>
            <button type="submit" className="btn btn-or btn-sm" disabled={saving}>Enregistrer</button>
          </div>
        </form>
      )}

      {tab === 'password' && (
        <form onSubmit={savePwd}>
          <div className="field"><label>Mot de passe actuel</label>
            <input type="password" value={pwd.currentPassword}
              onChange={(e) => setPwd({ ...pwd, currentPassword: e.target.value })} required />
          </div>
          <div className="field"><label>Nouveau mot de passe</label>
            <input type="password" value={pwd.newPassword} minLength={6}
              onChange={(e) => setPwd({ ...pwd, newPassword: e.target.value })} required />
          </div>
          {err && <div className="msg-erreur" style={{ marginBottom: 12 }}>{err}</div>}
          <div className="compte-actions-btns">
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setTab(null)}>Annuler</button>
            <button type="submit" className="btn btn-or btn-sm" disabled={saving}>Enregistrer</button>
          </div>
        </form>
      )}
    </div>
  );
}

const formatEventDates = (event) => {
  const s = String(event.startDate).slice(0, 10);
  const e = String(event.endDate).slice(0, 10);
  const fmt = (d) => new Date(`${d}T12:00:00`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });
  const serviceLabel = event.service === 'lunch' ? ' (midi)' : event.service === 'dinner' ? ' (soir)' : '';
  return s === e ? `${fmt(s)}${serviceLabel}` : `du ${fmt(s)} au ${fmt(e)}${serviceLabel}`;
};

function AuthForms() {
  const { setCustomer, loyaltySettings } = useStore();
  const [mode, setMode] = useState('register');
  const [form, setForm] = useState({ name: '', phone: '', email: '', password: '', marketingOptin: true });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const welcome = parseInt(loyaltySettings?.welcomePoints) || 0;

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const path = mode === 'login' ? '/customers/login' : '/customers/register';
      const body = mode === 'login'
        ? { phone: form.phone, password: form.password }
        : form;

      const response = await fetch(`${API_URL}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await response.json();

      if (response.ok && data.success) {
        setCustomerToken(data.token);
        setCustomer(data.customer);
      } else if (data.errors?.length) {
        setError(data.errors[0].msg);
      } else {
        setError(data.message || 'Une erreur est survenue');
      }
    } catch {
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card card-pad auth-carte">
      <div className="auth-switch">
        <button className={mode === 'register' ? 'active' : ''} onClick={() => { setMode('register'); setError(''); }}>
          Créer un compte
        </button>
        <button className={mode === 'login' ? 'active' : ''} onClick={() => { setMode('login'); setError(''); }}>
          J'ai déjà un compte
        </button>
      </div>

      {mode === 'register' && welcome > 0 && (
        <div className="auth-welcome">
          <IconStar size={18} filled /> {welcome} points offerts à l'inscription
        </div>
      )}

      <form onSubmit={submit}>
        {mode === 'register' && (
          <div className="field">
            <label>Nom</label>
            <input
              type="text"
              value={form.name}
              placeholder="Votre nom"
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              minLength={2}
            />
          </div>
        )}

        <div className="field">
          <label>Téléphone</label>
          <input
            type="tel"
            value={form.phone}
            placeholder="06 12 34 56 78"
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            required
          />
        </div>

        {mode === 'register' && (
          <div className="field">
            <label>Email</label>
            <input
              type="email"
              value={form.email}
              placeholder="vous@exemple.fr"
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
            />
          </div>
        )}

        <div className="field">
          <label>Mot de passe</label>
          <input
            type="password"
            value={form.password}
            placeholder={mode === 'register' ? '6 caractères minimum' : 'Votre mot de passe'}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            required
            minLength={mode === 'register' ? 6 : undefined}
          />
        </div>

        {mode === 'register' && (
          <label className="auth-optin">
            <input
              type="checkbox"
              checked={form.marketingOptin}
              onChange={(e) => setForm({ ...form, marketingOptin: e.target.checked })}
            />
            Je veux recevoir les offres et soirées du Fief par email
          </label>
        )}

        {error && <div className="msg-erreur" style={{ marginBottom: 14 }}>{error}</div>}

        <button type="submit" className="btn btn-or btn-block btn-lg" disabled={loading}>
          {loading ? 'Un instant…' : mode === 'register' ? 'Créer mon compte' : 'Me connecter'}
        </button>

        {mode === 'login' && (
          <p className="auth-oubli">
            Mot de passe oublié ? Appelez-nous au <a href="tel:0749110186">07 49 11 01 86</a>,
            on le réinitialise en un instant.
          </p>
        )}
      </form>
    </div>
  );
}

// Carte d'une récompense (produit échangeable en points)
function RewardCard({ product, balance, inCart, onAdd }) {
  const affordable = balance >= product.points_price;
  const missing = product.points_price - balance;

  return (
    <div className={`reward-carte ${affordable ? '' : 'reward-locked'}`}>
      <div className="reward-carte-haut">
        <h4 className="reward-nom">{product.name}</h4>
        <span className="reward-valeur">{parseFloat(product.price).toFixed(2)}€</span>
      </div>
      {product.description && <p className="reward-desc">{product.description}</p>}
      <div className="reward-carte-bas">
        <span className="reward-pts">
          <IconStar size={14} filled /> {product.points_price} pts
        </span>
        {affordable ? (
          <button
            className={`reward-add ${inCart > 0 ? 'ajoute' : ''}`}
            onClick={() => onAdd(product)}
          >
            {inCart > 0 ? <><IconCheck size={15} /> {inCart} au panier</> : <><IconPlus size={15} /> Ajouter</>}
          </button>
        ) : (
          <span className="reward-manque">Encore {missing} pts</span>
        )}
      </div>
    </div>
  );
}

function AccountPage() {
  const {
    customer, logoutCustomer, loyaltySettings, loyaltyEvents,
    addReward, cart, pointsReserved
  } = useStore();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [flash, setFlash] = useState(false);

  const loyalty = customer?.loyalty || loyaltySettings;
  const events = customer?.events || loyaltyEvents || [];
  const enabled = Boolean(loyalty && loyalty.enabled);
  const welcomePoints = parseInt(loyalty?.welcomePoints) || 0;

  // Catalogue des produits échangeables (triés du - cher au + cher en points)
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/products`);
        if (!res.ok) return;
        const list = await res.json();
        setProducts(
          list
            .filter(p => p.points_price && p.available && p.can_take_away)
            .sort((a, b) => a.points_price - b.points_price)
        );
      } catch { /* silencieux */ }
    })();
  }, []);

  const balance = customer?.balance || 0;
  const available = balance - pointsReserved; // solde réellement dépensable
  const rewardsInCart = cart.filter(i => i.redeemed);
  const inCartCount = (productId) =>
    rewardsInCart.filter(i => i.id === productId).reduce((s, i) => s + i.quantity, 0);

  const unlocked = products.filter(p => p.points_price <= balance);
  const locked = products.filter(p => p.points_price > balance);

  const handleAdd = (product) => {
    const ok = addReward(product);
    if (ok) {
      setFlash(true);
      setTimeout(() => setFlash(false), 1500);
    }
  };

  return (
    <div className="container-page compte-page">
      <div className="compte-entete">
        <h1>Le club Le Fief</h1>
        <p>
          Cumulez des points à chaque commande et échangez-les contre vos
          produits préférés. Simple, gratuit, gourmand.
        </p>
      </div>

      {!customer ? (
        <div className="compte-colonnes">
          <div className="compte-principal">
            <AuthForms />
          </div>
          <aside className="compte-cote">
            <div className="card card-pad compte-regles">
              <h3>Comment ça marche ?</h3>
              <ol>
                <li><strong>1€ dépensé = {enabled ? loyalty.pointsPerEuro : 1} point{enabled && loyalty.pointsPerEuro > 1 ? 's' : ''}</strong>, crédité au retrait.</li>
                <li>Vos points vous offrent des <strong>produits de la carte</strong>.</li>
                <li>Ajoutez votre récompense au panier, elle est déduite de vos points.</li>
              </ol>
            </div>
            {products.length > 0 && (
              <div className="card card-pad compte-apercu">
                <h3>🎁 Un aperçu des récompenses</h3>
                {products.slice(0, 6).map(p => (
                  <div key={p.id} className="apercu-ligne">
                    <span>{p.name}</span>
                    <span className="apercu-pts">{p.points_price} pts</span>
                  </div>
                ))}
                <p className="apercu-cta">
                  Créez un compte pour commencer à cumuler
                  {welcomePoints > 0 ? ` — ${welcomePoints} points offerts à l'inscription !` : ' !'}
                </p>
              </div>
            )}
          </aside>
        </div>
      ) : (
        <>
          {/* ---- Bandeau solde ---- */}
          <div className={`solde-bandeau ${flash ? 'flash' : ''}`}>
            <div className="solde-gauche">
              <p className="solde-bonjour">Bonjour {customer.name}</p>
              <div className="solde-points">
                <span className="solde-nombre">{available}</span>
                <span className="solde-unite">point{available > 1 ? 's' : ''} à dépenser</span>
              </div>
              {pointsReserved > 0 && (
                <p className="solde-reserve">{pointsReserved} pts déjà réservés dans votre panier</p>
              )}
            </div>
            <div className="solde-droite">
              {rewardsInCart.length > 0 ? (
                <button className="btn btn-or" onClick={() => navigate('/commande')}>
                  Finaliser ({rewardsInCart.reduce((s, i) => s + i.quantity, 0)} offert{rewardsInCart.reduce((s, i) => s + i.quantity, 0) > 1 ? 's' : ''})
                </button>
              ) : (
                <button className="btn btn-ghost-or" onClick={() => navigate('/')}>Voir la carte</button>
              )}
              <button className="solde-logout" onClick={logoutCustomer}>Se déconnecter</button>
            </div>
          </div>

          {!enabled ? (
            <div className="card card-pad compte-pause">Le programme de fidélité est momentanément en pause.</div>
          ) : (
            <div className="compte-colonnes-large">
              <div className="compte-recompenses">
                {/* ---- Disponibles maintenant ---- */}
                <h2 className="section-title">À échanger maintenant</h2>
                {unlocked.length === 0 ? (
                  <div className="card card-pad reward-vide">
                    Pas encore de récompense à votre portée — encore un peu de patience,
                    vos prochaines commandes vont vous rapprocher !
                  </div>
                ) : (
                  <div className="reward-grille">
                    {unlocked.map(p => (
                      <RewardCard
                        key={p.id}
                        product={p}
                        balance={available}
                        inCart={inCartCount(p.id)}
                        onAdd={handleAdd}
                      />
                    ))}
                  </div>
                )}

                {/* ---- À débloquer ---- */}
                {locked.length > 0 && (
                  <>
                    <h2 className="section-title">À débloquer bientôt</h2>
                    <div className="reward-grille">
                      {locked.map(p => (
                        <RewardCard
                          key={p.id}
                          product={p}
                          balance={balance}
                          inCart={0}
                          onAdd={handleAdd}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>

              <aside className="compte-cote">
                <MyAccountCard />

                <div className="card card-pad compte-regles">
                  <h3>Comment ça marche ?</h3>
                  <ol>
                    <li><strong>1€ = {loyalty.pointsPerEuro} point{loyalty.pointsPerEuro > 1 ? 's' : ''}</strong>, crédité au retrait de la commande.</li>
                    <li>Ajoutez une récompense au panier ci-contre.</li>
                    <li>Elle passe <strong>« Offert »</strong> et vos points sont déduits à la commande.</li>
                  </ol>
                </div>

                {events.length > 0 && (
                  <div className="card card-pad compte-events">
                    <h3>🔥 Prochaines soirées</h3>
                    {events.map(event => (
                      <div key={event.id} className="compte-event">
                        <span className="pill pill-or-plein">x{parseFloat(event.multiplier)}</span>
                        <div>
                          <strong>{event.name}</strong>
                          <p>{formatEventDates(event)} — points multipliés !</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {customer.history?.length > 0 && (
                  <div className="card card-pad compte-historique">
                    <h3>Mes derniers points</h3>
                    {customer.history.slice(0, 8).map(tx => (
                      <div key={tx.id} className="compte-tx">
                        <span className="compte-tx-label">{tx.label || tx.type}</span>
                        <span className={`compte-tx-points ${tx.points >= 0 ? 'plus' : 'moins'}`}>
                          {tx.points >= 0 ? '+' : ''}{tx.points}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </aside>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default AccountPage;
